'use client'
import { useState, useRef, useEffect, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import AvailabilityCalendar from '@/components/AvailabilityCalendar'
import { createClient } from '@/lib/supabase/client'

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!)
const PROPERTY_ID = 'cfa302c9-97aa-4e88-9297-c3e34b07acde'

// ─── Property Details ──────────────────────────────────────
const PROPERTY = {
  name: 'The Coastal Casita',
  shortName: 'The Coastal Casita · PCB',
  address: '5801 Thomas Dr, Unit 225',
  city: 'Panama City Beach, FL 32408',
  beds: 3, baths: 2, sleeps: 8,
  checkinTime: '4:00 PM', checkoutTime: '10:00 AM',
  email: 'richfranco@premierpropertymgt.net',
  phone: '(855) 596-3948',
}

// ─── Pricing ───────────────────────────────────────────────
const NIGHTLY_DEFAULT = 375, CLEANING = 50, TAX_RATE = 0.08
const DAMAGE_DEPOSIT = 150
const EXTENDED_STAY_THRESHOLD = 5, EXTENDED_STAY_DISCOUNT = 0.08
const TIMER_DISCOUNT = 0.05
const TIMER_DURATION = 20 * 60 // 20 minutes in seconds
const SPLIT_PAYMENT_REMAINDER_DISCOUNT = 0.10 // 10% off the second half

const AMENITIES = [
  { icon: '🌊', label: 'Direct beachfront access' },
  { icon: '🛏', label: '3 bedrooms' },
  { icon: '🚿', label: '2 full bathrooms' },
  { icon: '👥', label: 'Sleeps up to 8 guests' },
  { icon: '🏊', label: 'Community pool' },
  { icon: '🍳', label: 'Full kitchen' },
  { icon: '📺', label: 'Smart TVs in all rooms' },
  { icon: '🅿️', label: 'Free covered parking' },
  { icon: '🌅', label: 'Gulf views from balcony' },
  { icon: '❄️', label: 'Central A/C' },
  { icon: '🧺', label: 'In-unit washer/dryer' },
  { icon: '📶', label: 'High-speed WiFi' },
]

interface BookingState {
  checkin: string; checkout: string; nights: number; guests: number
  firstName: string; lastName: string; email: string; phone: string
  address: string; cityState: string; zip: string; idType: string
}
type Step = 'dates' | 'details' | 'agreement' | 'payment' | 'complete'
type ActivePromo = 'extended_stay' | 'timer' | 'both' | 'none'

function getActivePromo(nights: number, timerActive: boolean): ActivePromo {
  const extendedStay = nights > EXTENDED_STAY_THRESHOLD
  if (extendedStay && timerActive) return 'both'
  if (extendedStay) return 'extended_stay'
  if (timerActive) return 'timer'
  return 'none'
}

function calcFees(nights: number, nightlyRate: number, promo: ActivePromo = 'none') {
  const rentalFull = nights * nightlyRate
  const discountRate =
    promo === 'both' ? EXTENDED_STAY_DISCOUNT + TIMER_DISCOUNT :
    promo === 'extended_stay' ? EXTENDED_STAY_DISCOUNT :
    promo === 'timer' ? TIMER_DISCOUNT : 0
  const discount = rentalFull * discountRate
  const rental = rentalFull - discount
  const tax = rental * TAX_RATE
  const feesAndTaxes = CLEANING + tax
  return { rentalFull, discount, rental, tax, grand: rental + CLEANING + tax, feesAndTaxes, promo }
}

function formatTimer(secs: number) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0')
  const s = (secs % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}
function fmt(n: number) { return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
function genId() { const c='ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; let id='PPM-'; for(let i=0;i<8;i++)id+=c[Math.floor(Math.random()*c.length)]; return id }

interface PaymentFormProps {
  amount: number
  splitPayment: boolean
  remainingAmount: number
  checkin: string
  onSuccess: (id: string) => void
  onBack: () => void
}

function PaymentForm({ amount, splitPayment, remainingAmount, checkin, onSuccess, onBack }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')

  // Calculate remaining charge date label
  const remainingLabel = (() => {
    const d = new Date(checkin)
    d.setDate(d.getDate() - 7)
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  })()

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setProcessing(true)
    setError('')
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + '/book' },
      redirect: 'if_required',
    })
    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed. Please try again.')
      setProcessing(false)
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id)
    }
  }

  return (
    <form onSubmit={handlePay}>
      <div className="panel-title">
        <h2>Secure Payment</h2>
        <p>{splitPayment ? 'Pay half now — the remaining balance is charged automatically 7 days before arrival.' : 'Pay by card, or choose Klarna or Afterpay at checkout to split into installments.'}</p>
      </div>
      <div className="payment-amount-box">
        <div className="pay-label">Due Today</div>
        <div className="pay-amount">${fmt(amount)}</div>
        {splitPayment ? (
          <div className="pay-sub" style={{color:'#e8b84b'}}>
            Remaining ${fmt(remainingAmount)} charged {remainingLabel} (10% off)
          </div>
        ) : (
          <div className="pay-sub">Full stay amount — nothing more due before arrival</div>
        )}
      </div>
      <div style={{margin:'1.5rem 0'}}>
        <PaymentElement options={{ layout: 'accordion' }} />
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="btn-row">
        <button type="button" className="ghost-btn" onClick={onBack}>← Back</button>
        <button type="submit" className="primary-btn" disabled={!stripe || processing}>
          {processing ? 'Processing...' : `Pay $${fmt(amount)} Now →`}
        </button>
      </div>
      <p style={{fontSize:11,color:'#8a9baa',textAlign:'center',marginTop:'1rem'}}>
        🔒 Secured by Stripe · Pay with card, Klarna, Afterpay &amp; more
      </p>
    </form>
  )
}

export default function BookPage() {
  const [step, setStep] = useState<Step>('dates')
  const [booking, setBooking] = useState<BookingState>({ checkin:'',checkout:'',nights:0,guests:2,firstName:'',lastName:'',email:'',phone:'',address:'',cityState:'',zip:'',idType:'' })
  const [error, setError] = useState('')
  const [sigMode, setSigMode] = useState<'draw'|'type'>('draw')
  const [typedSig, setTypedSig] = useState('')
  const [sigName, setSigName] = useState('')
  const [checks, setChecks] = useState({ c1:false,c2:false,c3:false,c4:false })
  const [inits, setInits] = useState({ i1:'',i2:'',i3:'',i4:'' })
  const [submitting, setSubmitting] = useState(false)
  const [agreementId, setAgreementId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [stripePaymentId, setStripePaymentId] = useState('')
  const [nightly, setNightly] = useState(NIGHTLY_DEFAULT)
  const [surchargeReasons, setSurchargeReasons] = useState<string[]>([])
  const [photos, setPhotos] = useState<string[]>([])
  const [lightboxIdx, setLightboxIdx] = useState(-1)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false), hasSigRef = useRef(false)
  const [timerSeconds, setTimerSeconds] = useState(TIMER_DURATION)
  const [timerExpired, setTimerExpired] = useState(false)
  const [promosEnabled, setPromosEnabled] = useState({ timer_discount: false, split_payment: false })
  const [splitPaymentChosen, setSplitPaymentChosen] = useState(false)
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null)
  const timerActive = promosEnabled.timer_discount && timerSeconds > 0 && !timerExpired
  const canSplitPay = promosEnabled.split_payment && booking.nights <= EXTENDED_STAY_THRESHOLD
  const activePromo = getActivePromo(booking.nights, timerActive && !splitPaymentChosen)
  const fees = calcFees(booking.nights, nightly, activePromo)
  const feesBase = calcFees(booking.nights, nightly, 'none')
  const splitFirstAmount = feesBase.grand / 2
  const splitRemainingAmount = (feesBase.grand / 2) * (1 - SPLIT_PAYMENT_REMAINDER_DISCOUNT)

  // Restore state after Klarna / Afterpay redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const redirectStatus = params.get('redirect_status')
    const paymentIntentId = params.get('payment_intent')
    if (redirectStatus === 'succeeded' && paymentIntentId) {
      try {
        const saved = sessionStorage.getItem('ppm_redirect_state')
        if (saved) {
          const s = JSON.parse(saved)
          if (s.booking) setBooking(s.booking)
          if (s.agreementId) setAgreementId(s.agreementId)
          if (s.nightly) setNightly(s.nightly)
          if (s.splitPaymentChosen) setSplitPaymentChosen(s.splitPaymentChosen)
          if (s.stripeCustomerId) setStripeCustomerId(s.stripeCustomerId)
          setStripePaymentId(paymentIntentId)
          sessionStorage.removeItem('ppm_redirect_state')
          window.history.replaceState({}, '', '/book')
          if (s.booking && s.agreementId) {
            const restoredFees = calcFees(s.booking.nights ?? 0, s.nightly ?? NIGHTLY_DEFAULT)
            const grand = s.grand ?? restoredFees.grand
            const splitChosen = s.splitPaymentChosen ?? false
            const custId = s.stripeCustomerId ?? null
            const remainingCents = splitChosen ? Math.round((grand / 2) * (1 - SPLIT_PAYMENT_REMAINDER_DISCOUNT) * 100) : undefined
            sendConfirmation(paymentIntentId, s.booking, s.agreementId, s.nightly ?? NIGHTLY_DEFAULT, splitChosen ? grand / 2 : grand, restoredFees.discount, splitChosen, custId, remainingCents)
          }
          setStep('complete')
        }
      } catch {}
    }
  }, [])

  useEffect(() => {
    createClient()
      .from('property_photos')
      .select('url')
      .eq('property_id', PROPERTY_ID)
      .order('position')
      .then(({ data }) => { if (data?.length) setPhotos(data.map(p => p.url)) })
  }, [])

  useEffect(() => {
    const url = booking.checkin
      ? `/api/book/property-rate?checkin=${booking.checkin}`
      : '/api/book/property-rate'
    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (d.nightly_rate) setNightly(d.nightly_rate)
        setSurchargeReasons(d.surcharge_reasons ?? [])
      })
      .catch(() => {})
  }, [booking.checkin])

  useEffect(() => {
    fetch('/api/promos')
      .then(r => r.json())
      .then(d => setPromosEnabled(d))
      .catch(() => {})
  }, [])

  useEffect(() => {
    const TIMER_KEY = 'ppm_timer_start'
    let start = parseInt(sessionStorage.getItem(TIMER_KEY) || '0', 10)
    if (!start) {
      start = Date.now()
      sessionStorage.setItem(TIMER_KEY, String(start))
    }
    const elapsed = Math.floor((Date.now() - start) / 1000)
    const initial = Math.max(0, TIMER_DURATION - elapsed)
    if (initial === 0) { setTimerExpired(true); return }
    setTimerSeconds(initial)
    const intervalId = setInterval(() => {
      setTimerSeconds(s => {
        if (s <= 1) { clearInterval(intervalId); setTimerExpired(true); return 0 }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    if (step !== 'agreement') return
    setTimeout(() => {
      const canvas = canvasRef.current; if (!canvas) return
      canvas.width = canvas.offsetWidth; canvas.height = 150
      const ctx = canvas.getContext('2d'); if (!ctx) return
      function getPos(e: MouseEvent|TouchEvent) {
        const r = canvas!.getBoundingClientRect(), src = 'touches' in e ? e.touches[0] : e
        return { x: src.clientX-r.left, y: src.clientY-r.top }
      }
      canvas.addEventListener('mousedown', e => { drawingRef.current=true; const p=getPos(e); ctx.beginPath(); ctx.moveTo(p.x,p.y) })
      canvas.addEventListener('mousemove', e => { if(!drawingRef.current)return; hasSigRef.current=true; const p=getPos(e); ctx.lineTo(p.x,p.y); ctx.strokeStyle='#0f2240'; ctx.lineWidth=2; ctx.lineCap='round'; ctx.stroke() })
      canvas.addEventListener('mouseup', () => { drawingRef.current=false })
      canvas.addEventListener('mouseleave', () => { drawingRef.current=false })
      canvas.addEventListener('touchstart', e => { e.preventDefault(); drawingRef.current=true; const p=getPos(e); ctx.beginPath(); ctx.moveTo(p.x,p.y) }, {passive:false})
      canvas.addEventListener('touchmove', e => { e.preventDefault(); if(!drawingRef.current)return; hasSigRef.current=true; const p=getPos(e); ctx.lineTo(p.x,p.y); ctx.strokeStyle='#0f2240'; ctx.lineWidth=2.5; ctx.lineCap='round'; ctx.stroke() }, {passive:false})
      canvas.addEventListener('touchend', () => { drawingRef.current=false })
    }, 100)
  }, [step])

  function clearCanvas() { const c=canvasRef.current; if(c){c.getContext('2d')?.clearRect(0,0,c.width,c.height); hasSigRef.current=false} }

  function sendConfirmation(paymentIntentId: string, bk: BookingState, agreeId: string, nightlyRate: number, total: number, discount: number, splitPayment?: boolean, custId?: string | null, remainingCents?: number) {
    fetch('/api/book/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agreementId: agreeId,
        firstName: bk.firstName,
        lastName: bk.lastName,
        email: bk.email,
        phone: bk.phone,
        checkin: bk.checkin,
        checkout: bk.checkout,
        nights: bk.nights,
        guests: bk.guests,
        total,
        nightly: nightlyRate,
        discount,
        paymentIntentId,
        splitPayment: splitPayment ?? false,
        stripeCustomerId: custId ?? undefined,
        remainingAmountCents: remainingCents,
      }),
    }).catch(err => console.error('Confirmation email failed:', err))
  }

  function validateDetails() {
    const {firstName,lastName,email,phone,address,cityState,zip,idType} = booking
    if (!firstName||!lastName||!email||!phone||!address||!cityState||!zip||!idType) { setError('Please fill in all required fields.'); return false }
    if (!/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email address.'); return false }
    setError(''); return true
  }
  function validateAgreement() {
    if (!sigName.trim()) return 'Please enter your full legal name.'
    if (!checks.c1||!checks.c2||!checks.c3||!checks.c4) return 'Please check all consent boxes.'
    if (!inits.i1||!inits.i2||!inits.i3||!inits.i4) return 'Please initial all required sections.'
    if (sigMode==='draw'&&!hasSigRef.current) return 'Please draw your signature.'
    if (sigMode==='type'&&!typedSig.trim()) return 'Please type your signature.'
    return ''
  }
  async function submitAgreement() {
    const err = validateAgreement(); if (err) { setError(err); return }
    setSubmitting(true); setError('')

    const id = genId()

    // Capture canvas signature as base64 if in draw mode
    let signatureData = typedSig
    if (sigMode === 'draw' && canvasRef.current) {
      signatureData = canvasRef.current.toDataURL('image/png')
    }

    try {
      const res = await fetch('/api/book/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agreement_id:     id,
          first_name:       booking.firstName,
          last_name:        booking.lastName,
          email:            booking.email,
          phone:            booking.phone,
          guest_address:    booking.address,
          guest_city_state: booking.cityState,
          guest_zip:        booking.zip,
          id_type:          booking.idType,
          property_address: PROPERTY.address,
          checkin:          booking.checkin,
          checkout:         booking.checkout,
          nights:           booking.nights,
          guests:           booking.guests,
          nightly_rate:     nightly * 100,
          cleaning_fee:     CLEANING * 100,
          service_fee:      0,
          tax:              Math.round(fees.tax * 100),
          total:            Math.round(fees.grand * 100),
          damage_deposit:   DAMAGE_DEPOSIT * 100,
          signature_type:   sigMode,
          signature_data:   signatureData,
          sig_name:         sigName,
        }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to save agreement')
      }

      setAgreementId(id)
      if (canSplitPay) {
        sessionStorage.setItem('ppm_redirect_state', JSON.stringify({ agreementId: id, booking, nightly, grand: feesBase.grand }))
        setStep('payment')
      } else {
        const intentRes = await fetch('/api/payments/create-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payment_type: 'booking_deposit',
            amount_cents: Math.round(fees.grand * 100),
            guest_email: booking.email,
            metadata: { agreement_id: id, checkin: booking.checkin, checkout: booking.checkout },
          }),
        })
        if (!intentRes.ok) { const d = await intentRes.json(); throw new Error(d.error || 'Failed to initialize payment') }
        const { client_secret, stripe_customer_id } = await intentRes.json()
        if (stripe_customer_id) setStripeCustomerId(stripe_customer_id)
        sessionStorage.setItem('ppm_redirect_state', JSON.stringify({ agreementId: id, booking, nightly, grand: fees.grand, stripeCustomerId: stripe_customer_id ?? null }))
        setClientSecret(client_secret)
        setStep('payment')
      }
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again or contact us directly.')
    } finally {
      setSubmitting(false)
    }
  }

  async function createPaymentIntent(splitChosen: boolean) {
    const amountCents = splitChosen ? Math.round(splitFirstAmount * 100) : Math.round(fees.grand * 100)
    setError('')
    try {
      const body: Record<string, unknown> = {
        payment_type: splitChosen ? 'booking_split_first' : 'booking_deposit',
        amount_cents: amountCents,
        metadata: { agreement_id: agreementId, checkin: booking.checkin, checkout: booking.checkout },
      }
      if (splitChosen) body.guest_email = booking.email
      const res = await fetch('/api/payments/create-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Payment initialization failed') }
      const { client_secret, stripe_customer_id } = await res.json()
      setSplitPaymentChosen(splitChosen)
      if (stripe_customer_id) setStripeCustomerId(stripe_customer_id)
      sessionStorage.setItem('ppm_redirect_state', JSON.stringify({
        agreementId, booking, nightly, grand: splitChosen ? feesBase.grand : fees.grand,
        splitPaymentChosen: splitChosen, stripeCustomerId: stripe_customer_id ?? null,
      }))
      setClientSecret(client_secret)
    } catch (e: any) {
      setError(e.message || 'Payment initialization failed. Please try again.')
    }
  }

  function StepIndicator() {
    const steps: {key:Step;label:string}[] = [{key:'dates',label:'Select Dates'},{key:'details',label:'Your Info'},{key:'agreement',label:'Agreement'},{key:'payment',label:'Payment'},{key:'complete',label:'Confirmed'}]
    const idx = steps.findIndex(s => s.key===step)
    return (<div className="step-indicator">{steps.map((s,i) => (<div key={s.key} className={`step-item ${i<=idx?'active':''} ${i<idx?'done':''}`}><div className="step-circle">{i<idx?'✓':i+1}</div><span className="step-label">{s.label}</span>{i<steps.length-1&&<div className="step-line"/>}</div>))}</div>)
  }

  function PriceSummary() {
    if (!booking.nights) return null
    const note = splitPaymentChosen
      ? `$${fmt(splitFirstAmount)} due today · $${fmt(splitRemainingAmount)} charged 7 days before arrival`
      : 'Full amount due at booking · Damage deposit charged automatically 7 days before arrival'
    const discountRows = activePromo === 'both' ? (
      <>
        <div className="ps-row ps-discount"><span>Extended stay discount (8%)</span><span>−${fmt(fees.rentalFull * EXTENDED_STAY_DISCOUNT)}</span></div>
        <div className="ps-row ps-discount"><span>Timer discount (5%)</span><span>−${fmt(fees.rentalFull * TIMER_DISCOUNT)}</span></div>
      </>
    ) : fees.discount > 0 ? (
      <div className="ps-row ps-discount"><span>{activePromo === 'extended_stay' ? 'Extended stay discount (8%)' : 'Timer discount (5%)'}</span><span>−${fmt(fees.discount)}</span></div>
    ) : null
    return (<div className="price-summary"><div className="ps-row"><span>${nightly}/night × {booking.nights} nights</span><span>${fmt(fees.rentalFull)}</span></div>{discountRows}{surchargeReasons.map(r=><div key={r} className="ps-row ps-surcharge"><span>⚡ {r}</span><span>included</span></div>)}<div className="ps-row"><span>Taxes &amp; fees</span><span>${fmt(fees.feesAndTaxes)}</span></div><div className="ps-divider"/><div className="ps-row total"><strong>Total</strong><strong>${fmt(fees.grand)}</strong></div><div className="ps-row deposit-row"><span>★ Refundable damage deposit</span><span>${fmt(DAMAGE_DEPOSIT)}</span></div><div className="ps-note">{note}</div></div>)
  }

  const legalClauses = [
    ['1. Parties', `This Vacation Rental Agreement is between Premier Property MGT ("Host/Manager") and ${booking.firstName||'Guest'} ${booking.lastName||''} ("Guest"), binding under Florida law.`],
    ['2. Property & Rental Period', `Property: ${PROPERTY.address}, ${PROPERTY.city} — a ${PROPERTY.beds}-bedroom, ${PROPERTY.baths}-bathroom beachfront condo sleeping up to ${PROPERTY.sleeps} guests. Rental begins ${PROPERTY.checkinTime} on ${booking.checkin} and ends ${PROPERTY.checkoutTime} on ${booking.checkout}. Overstay fee: $150/hr. Access details sent 24 hrs before arrival.`],
    ['3. Payment & Card Authorization', `Guest authorizes Premier Property MGT to charge card on file for: (a) full rental amount at booking; (b) $${DAMAGE_DEPOSIT} refundable damage deposit automatically charged 7 days before arrival; (c) any additional charges including damages, late fees, unauthorized guests, or excessive cleaning.`],
    ['4. Cancellation Policy', `More than 30 days before arrival: Full refund minus the $${CLEANING} cleaning fee. 8–30 days before arrival: 50% refund; cleaning fee non-refundable. 7 days or fewer before arrival: No refund. No-shows: No refund. The cleaning fee is non-refundable in all cases. No refunds issued for weather, road conditions, or government travel advisories unless property is uninhabitable.`],
    ['5. Damage Deposit', `A $${DAMAGE_DEPOSIT} refundable security deposit is automatically charged 7 days before arrival. Returned within 48 hours of checkout pending satisfactory inspection. Deductions may be made for: property damage beyond normal wear, excessive cleaning, missing items, unauthorized pets or guests, or house rule violations. Guest authorizes additional charges exceeding deposit amount.`],
    ['6. Occupancy Limits', `Maximum occupancy is ${PROPERTY.sleeps} persons including children. Violations result in $500/unauthorized guest/night plus possible immediate eviction without refund. Day visitors not listed on reservation require prior written Host approval.`],
    ['7. House Rules', 'No smoking anywhere on premises (inside, balcony, or beach-side) — $500 minimum remediation fee. No parties, events, or gatherings. No pets without prior written approval ($300 deep-clean fee). Quiet hours 10 PM–8 AM per Bay County ordinance. All trash bagged and placed in exterior bins. Lock all doors and windows on departure.'],
    ['8. Noise & Local Ordinances', 'Bay County and Panama City Beach enforce strict noise ordinances. Exterior noise must not exceed 60 dB between 10 PM–8 AM. Guest is solely responsible for all fines and citations. Police or security response due to Guest violations may result in immediate termination without refund.'],
    ['9. Liability & Indemnification', 'Guest assumes all risk of injury, loss, or damage arising from use of the property, balcony, pool, beach access, and all amenities. Guest agrees to hold harmless and indemnify Host, property owner, and Premier Property MGT from all claims, liabilities, and expenses including attorney fees.'],
    ['10. Property Condition Reporting', 'Guest agrees to inspect the property upon arrival and report any pre-existing damage within 2 hours of check-in via text or email to ' + PROPERTY.email + '. Failure to report pre-existing damage within this window may result in Guest being held responsible upon checkout.'],
    ['11. Right of Entry', 'Host may enter the property with 12 hours notice for inspection, maintenance, or repair. In case of emergency, Host may enter immediately without prior notice to protect property or guest safety.'],
    ['12. Governing Law & Dispute Resolution', 'This Agreement is governed by Florida law. Disputes submitted to mediation in Bay County, FL before litigation. Venue: Bay County, Florida. Prevailing party entitled to attorney fees and costs.'],
    ['13. Electronic Signature', 'Electronic signature constitutes a legally binding signature under the Florida Electronic Signature Act (F.S. §668.001 et seq.) and the federal E-SIGN Act (15 U.S.C. §7001). Guest IP address, device information, and timestamp are recorded with this transaction.'],
  ]

  return (
    <div className="book-page">
      {/* ── Property Hero ─────────────────────────────── */}
      <div className="prop-hero">
        <div className="prop-hero-inner">
          <a href="/" className="back-link">← Premier Property MGT</a>
          <div className="hero-badge">🌊 Beachfront · Direct Gulf Access</div>
          <h1>{PROPERTY.name}</h1>
          <p className="hero-address">📍 {PROPERTY.address}, {PROPERTY.city}</p>
          <div className="hero-stats">
            <div className="hero-stat"><span className="stat-num">{PROPERTY.beds}</span><span className="stat-lbl">Bedrooms</span></div>
            <div className="hero-divider"/>
            <div className="hero-stat"><span className="stat-num">{PROPERTY.baths}</span><span className="stat-lbl">Bathrooms</span></div>
            <div className="hero-divider"/>
            <div className="hero-stat"><span className="stat-num">{PROPERTY.sleeps}</span><span className="stat-lbl">Guests</span></div>
            <div className="hero-divider"/>
            <div className="hero-stat"><span className="stat-num">2nd</span><span className="stat-lbl">Floor</span></div>
          </div>
        </div>
      </div>

      {/* ── Amenities Bar ─────────────────────────────── */}
      <div className="amenity-bar">
        <div className="amenity-bar-inner">
          {AMENITIES.map(a => (<div key={a.label} className="amenity-chip"><span className="amenity-icon">{a.icon}</span><span>{a.label}</span></div>))}
        </div>
      </div>

      {/* ── Booking Body ──────────────────────────────── */}
      <div className="book-body">
        <div className="book-main">
          {timerActive && !splitPaymentChosen && step !== 'complete' && (
            <div className="timer-banner">
              {booking.nights > EXTENDED_STAY_THRESHOLD
                ? <>Book within <strong>{formatTimer(timerSeconds)}</strong> to stack an extra 5% on your long-stay discount — <strong>13% off total!</strong></>
                : <>Book within <strong>{formatTimer(timerSeconds)}</strong> and pay in full to get <strong>5% off</strong> your stay!</>
              }
            </div>
          )}
          <StepIndicator/>
          {step==='dates' && (
            <div className="step-panel">
              <div className="panel-title"><h2>Check Availability</h2><p>Select your check-in and check-out dates. Gold-shaded dates are already reserved.</p></div>
              <AvailabilityCalendar onDatesSelected={(ci,co,n)=>setBooking(b=>({...b,checkin:ci,checkout:co,nights:n}))}/>
              <div className="guests-row">
                <label>Number of guests</label>
                <select value={booking.guests} onChange={e=>setBooking(b=>({...b,guests:parseInt(e.target.value)}))}>
                  {[1,2,3,4,5,6,7,8].map(n=><option key={n}>{n}</option>)}
                </select>
                <span className="occupancy-note">Max {PROPERTY.sleeps} guests</span>
              </div>
              <button className="primary-btn" disabled={!booking.checkin||!booking.checkout} onClick={()=>setStep('details')}>Continue to Guest Info →</button>
            </div>
          )}

          {step==='details' && (
            <div className="step-panel">
              <div className="panel-title"><h2>Your Information</h2><p>Enter the primary guest details as they appear on your government-issued ID.</p></div>
              {error && <div className="form-error">{error}</div>}
              <div className="field-grid two">
                <div className="field-group"><label>First Name *</label><input value={booking.firstName} onChange={e=>setBooking(b=>({...b,firstName:e.target.value}))} placeholder="Legal first name"/></div>
                <div className="field-group"><label>Last Name *</label><input value={booking.lastName} onChange={e=>setBooking(b=>({...b,lastName:e.target.value}))} placeholder="Legal last name"/></div>
              </div>
              <div className="field-grid two">
                <div className="field-group"><label>Email Address *</label><input type="email" value={booking.email} onChange={e=>setBooking(b=>({...b,email:e.target.value}))} placeholder="your@email.com"/></div>
                <div className="field-group"><label>Phone Number *</label><input type="tel" value={booking.phone} onChange={e=>setBooking(b=>({...b,phone:e.target.value}))} placeholder="(xxx) xxx-xxxx"/></div>
              </div>
              <div className="field-grid three">
                <div className="field-group"><label>Street Address *</label><input value={booking.address} onChange={e=>setBooking(b=>({...b,address:e.target.value}))} placeholder="Home address"/></div>
                <div className="field-group"><label>City, State *</label><input value={booking.cityState} onChange={e=>setBooking(b=>({...b,cityState:e.target.value}))} placeholder="Atlanta, GA"/></div>
                <div className="field-group"><label>ZIP Code *</label><input value={booking.zip} onChange={e=>setBooking(b=>({...b,zip:e.target.value}))} placeholder="30301"/></div>
              </div>
              <div className="field-group" style={{maxWidth:280}}>
                <label>Government-Issued ID Type *</label>
                <select value={booking.idType} onChange={e=>setBooking(b=>({...b,idType:e.target.value}))}>
                  <option value="">Select ID type</option>
                  <option>Driver's License</option><option>Passport</option><option>State ID</option><option>Military ID</option>
                </select>
              </div>
              <div className="btn-row">
                <button className="ghost-btn" onClick={()=>setStep('dates')}>← Back</button>
                <button className="primary-btn" onClick={()=>{if(validateDetails())setStep('agreement')}}>Continue to Agreement →</button>
              </div>
            </div>
          )}

          {step==='agreement' && (
            <div className="step-panel">
              <div className="panel-title"><h2>Vacation Rental Agreement</h2><p>Please read and e-sign below to complete your reservation at {PROPERTY.address}.</p></div>
              <div className="agreement-doc">
                <div className="doc-header">
                  <div className="doc-logo">Premier Property MGT</div>
                  <div className="doc-title">Vacation Rental Agreement</div>
                  <div className="doc-prop">{PROPERTY.address} · {PROPERTY.city}</div>
                </div>
                <div className="booking-recap">
                  <div className="recap-item"><span>Guest</span><strong>{booking.firstName} {booking.lastName}</strong></div>
                  <div className="recap-item"><span>Check-in</span><strong>{booking.checkin} · {PROPERTY.checkinTime}</strong></div>
                  <div className="recap-item"><span>Check-out</span><strong>{booking.checkout} · {PROPERTY.checkoutTime}</strong></div>
                  <div className="recap-item"><span>Guests</span><strong>{booking.guests} of {PROPERTY.sleeps} max</strong></div>
                  <div className="recap-item"><span>Nights</span><strong>{booking.nights}</strong></div>
                  <div className="recap-item"><span>Total Due</span><strong>${fmt(fees.grand)}</strong></div>
                </div>
                <div className="legal-body">{legalClauses.map(([title,body])=>(<div key={title} className="legal-clause"><div className="clause-title">{title}</div><p>{body}</p></div>))}</div>
                <div className="initials-section">
                  <div className="initials-label">Initial each section to confirm understanding</div>
                  {[['i1','Payment & Cancellation Policy (Sections 3–4)'],['i2','Damage Deposit — authorize card charges (Section 5)'],['i3','Occupancy Limits & House Rules (Sections 6–7)'],['i4','Liability Waiver & Indemnification (Section 9)']].map(([k,label])=>(<div key={k} className="initial-row"><input className="initial-input" maxLength={3} placeholder="___" value={inits[k as keyof typeof inits]} onChange={e=>setInits(p=>({...p,[k]:e.target.value.toUpperCase()}))}/><span>{label}</span></div>))}
                </div>
                <div className="consent-section">
                  {[['c1','I am at least 25 years of age, the primary guest, and all information provided is accurate and truthful.'],['c2','I have read and agree to be legally bound by all terms of this Vacation Rental Agreement.'],['c3',`I expressly authorize Premier Property MGT to charge my card on file per the payment schedule and for any additional charges including the $${DAMAGE_DEPOSIT} refundable damage deposit.`],['c4','I understand my electronic signature is legally equivalent to a handwritten signature under Florida and federal law.']].map(([k,label])=>(<label key={k} className="consent-item"><input type="checkbox" checked={checks[k as keyof typeof checks]} onChange={e=>setChecks(p=>({...p,[k]:e.target.checked}))}/><span>{label}</span></label>))}
                </div>
                <div className="sig-section">
                  <div className="sig-label-row"><span>Signature</span><div className="sig-mode-toggle"><button className={sigMode==='draw'?'active':''} onClick={()=>setSigMode('draw')}>Draw</button><button className={sigMode==='type'?'active':''} onClick={()=>setSigMode('type')}>Type</button></div></div>
                  {sigMode==='draw'?(<div className="canvas-wrap"><canvas ref={canvasRef} className="sig-canvas"/><button className="clear-sig" onClick={clearCanvas}>Clear</button></div>):(<input className="typed-sig" value={typedSig} onChange={e=>setTypedSig(e.target.value)} placeholder="Type your full legal name"/>)}
                  <div className="field-group" style={{marginTop:'1rem'}}><label>Full Legal Name (must match ID on file)</label><input placeholder="Type your full legal name to confirm" value={sigName} onChange={e=>setSigName(e.target.value)}/></div>
                  <div className="timestamp">Timestamp: {new Date().toLocaleString('en-US',{month:'long',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit',timeZoneName:'short'})}</div>
                </div>
              </div>
              {error && <div className="form-error" style={{marginTop:'1rem'}}>{error}</div>}
              <div className="btn-row">
                <button className="ghost-btn" onClick={()=>setStep('details')}>← Back</button>
                <button className="primary-btn" onClick={submitAgreement} disabled={submitting}>{submitting?'Processing...':'✦ Sign & Complete Reservation'}</button>
              </div>
            </div>
          )}

          {step==='payment' && (
            <div className="step-panel">
              {canSplitPay && !clientSecret && (
                <>
                  <div className="panel-title"><h2>Choose How to Pay</h2><p>Select your payment option. Card details are entered after.</p></div>
                  {error && <div className="form-error">{error}</div>}
                  <div className="pay-options">
                    <div className="pay-option" onClick={() => createPaymentIntent(false)}>
                      <div className="pay-option-top">
                        <div>
                          <div className="pay-option-title">Pay in Full</div>
                          <div className="pay-option-sub">Full amount due now — nothing charged before check-in</div>
                        </div>
                        <div className="pay-option-amount">${fmt(fees.grand)}</div>
                      </div>
                      {timerActive && <div className="pay-option-check">✓ 5% timer discount applied</div>}
                    </div>
                    <div className="pay-option" onClick={() => createPaymentIntent(true)}>
                      <div className="pay-option-top">
                        <div>
                          <div className="pay-option-title">Pay Half Now · 10% Off Remainder</div>
                          <div className="pay-option-sub">${fmt(splitFirstAmount)} today · ${fmt(splitRemainingAmount)} charged 7 days before check-in</div>
                        </div>
                        <div className="pay-option-amount">${fmt(splitFirstAmount)}</div>
                      </div>
                      <div className="pay-option-check">✓ Save ${fmt((feesBase.grand / 2) * SPLIT_PAYMENT_REMAINDER_DISCOUNT)} on remaining balance</div>
                    </div>
                  </div>
                  <button className="ghost-btn" onClick={() => setStep('agreement')}>← Back</button>
                </>
              )}
              {clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#0f2240', borderRadius: '6px' } } }}>
                  <PaymentForm
                    amount={splitPaymentChosen ? splitFirstAmount : fees.grand}
                    splitPayment={splitPaymentChosen}
                    remainingAmount={splitRemainingAmount}
                    checkin={booking.checkin}
                    onSuccess={(id) => {
                      setStripePaymentId(id)
                      const remainingCents = splitPaymentChosen ? Math.round(splitRemainingAmount * 100) : undefined
                      sendConfirmation(id, booking, agreementId, nightly, splitPaymentChosen ? splitFirstAmount : fees.grand, fees.discount, splitPaymentChosen, stripeCustomerId, remainingCents)
                      setStep('complete')
                    }}
                    onBack={() => { if (canSplitPay) { setClientSecret('') } else { setStep('agreement') } }}
                  />
                </Elements>
              )}
            </div>
          )}

          {step==='complete' && (
            <div className="step-panel complete-panel">
              <div className="complete-icon">🎉</div>
              <h2>You&apos;re confirmed!</h2>
              <p>Your beachfront reservation is locked in. A signed copy of your agreement has been sent to <strong>{booking.email}</strong>.</p>
              <div className="confirm-details">
                <div className="cd-row"><span>Property</span><strong>{PROPERTY.name}</strong></div>
                <div className="cd-row"><span>Address</span><strong>{PROPERTY.address}, {PROPERTY.city}</strong></div>
                <div className="cd-row"><span>Check-in</span><strong>{booking.checkin} at {PROPERTY.checkinTime}</strong></div>
                <div className="cd-row"><span>Check-out</span><strong>{booking.checkout} at {PROPERTY.checkoutTime}</strong></div>
                <div className="cd-row"><span>Guests</span><strong>{booking.guests}</strong></div>
                <div className="cd-row"><span>Total Paid</span><strong>${fmt(fees.grand)}</strong></div>
                <div className="cd-row"><span>Damage Deposit</span><strong>${fmt(DAMAGE_DEPOSIT)} · charged automatically 7 days before arrival, refunded within 48 hrs of checkout</strong></div>
              </div>
              <div className="agreement-id">Agreement ID: {agreementId}</div>
              <p className="next-steps">Our team will reach out within 24 hours with check-in instructions, access codes, and everything you need for your stay. Questions? <a href={`mailto:${PROPERTY.email}`}>{PROPERTY.email}</a></p>
              <button className="ghost-btn" onClick={()=>window.print()}>Print / Save Agreement</button>
            </div>
          )}
        </div>

        {/* ── Sidebar ───────────────────────────────────── */}
        <aside className="book-sidebar">
          <div className="sidebar-card">
            <div className="prop-hero-img" onClick={() => photos.length && setLightboxIdx(0)} style={{cursor: photos.length ? 'pointer' : 'default'}}>
              {photos.length > 0 ? (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={photos[0]} alt="Property" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                  <div className="photo-count-badge">📷 View {photos.length} photo{photos.length !== 1 ? 's' : ''}</div>
                </>
              ) : (
                <>
                  <div className="prop-hero-img-inner">🌊</div>
                  <div className="prop-hero-img-label">Gulf-front · Panama City Beach</div>
                </>
              )}
            </div>
            <div className="prop-name">{PROPERTY.name}</div>
            <div className="prop-addr">📍 {PROPERTY.address}</div>
            <div className="prop-addr" style={{marginTop:2}}>{PROPERTY.city}</div>
            <div className="prop-meta">
              <span>🛏 {PROPERTY.beds} Bed</span>
              <span>🚿 {PROPERTY.baths} Bath</span>
              <span>👥 Sleeps {PROPERTY.sleeps}</span>
            </div>
            {booking.checkin && booking.checkout && (<><div className="sidebar-divider"/><div className="dates-recap"><div><span>Check-in</span> {booking.checkin} · {PROPERTY.checkinTime}</div><div><span>Check-out</span> {booking.checkout} · {PROPERTY.checkoutTime}</div><div><span>Guests</span> {booking.guests}</div></div><div className="sidebar-divider"/><PriceSummary/></>)}
          </div>
          <div className="trust-items">
            <div className="trust-item">🔒 Secure booking & signed agreement</div>
            <div className="trust-item">📋 Legally binding e-signature</div>
            <div className="trust-item">💳 ${DAMAGE_DEPOSIT} deposit returned within 48 hrs</div>
            <div className="trust-item">🌊 Direct beachfront access</div>
            <div className="trust-item">📞 24/7 guest support</div>
            <div className="trust-item">📍 <a href="https://maps.google.com/?q=5801+Thomas+Drive+Panama+City+Beach+FL" target="_blank" rel="noopener">View on Google Maps</a></div>
          </div>
        </aside>
      </div>

      {/* ── Photo Lightbox ───────────────────────────────── */}
      {lightboxIdx >= 0 && photos.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setLightboxIdx(-1)}>
          <button className="lightbox-close" onClick={() => setLightboxIdx(-1)}>✕</button>
          <div className="lightbox-counter">{lightboxIdx + 1} / {photos.length}</div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={photos[lightboxIdx]}
            alt={`Property photo ${lightboxIdx + 1}`}
            className="lightbox-img"
            onClick={e => e.stopPropagation()}
          />
          {photos.length > 1 && (
            <>
              <button className="lightbox-nav lightbox-prev" onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + photos.length) % photos.length) }}>‹</button>
              <button className="lightbox-nav lightbox-next" onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % photos.length) }}>›</button>
            </>
          )}
          <div className="lightbox-thumbs" onClick={e => e.stopPropagation()}>
            {photos.map((url, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img key={i} src={url} alt="" className={`lightbox-thumb ${i === lightboxIdx ? 'active' : ''}`} onClick={() => setLightboxIdx(i)} />
            ))}
          </div>
        </div>
      )}

      <style>{`
.book-page{min-height:100vh;background:#f8f7f4}

/* Hero */
.prop-hero{background:linear-gradient(160deg,#0a1a36 0%,#0f2240 60%,#1a3560 100%);padding:3rem 1.5rem 2.5rem;position:relative;overflow:hidden}
.prop-hero::before{content:'';position:absolute;inset:0;background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b8882a' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")}
.prop-hero-inner{max-width:1080px;margin:0 auto;position:relative}
.back-link{color:rgba(255,255,255,0.45);font-size:13px;text-decoration:none;display:inline-block;margin-bottom:1rem;transition:color .15s}
.back-link:hover{color:#b8882a}
.hero-badge{display:inline-block;background:rgba(184,136,42,0.2);border:1px solid rgba(184,136,42,0.4);color:#e8b84b;font-size:12px;letter-spacing:1px;padding:4px 12px;border-radius:20px;margin-bottom:1rem}
.prop-hero h1{font-family:'Playfair Display',Georgia,serif;color:#fff;font-size:2.4rem;font-weight:600;margin-bottom:.5rem;line-height:1.2}
.hero-address{color:rgba(255,255,255,0.6);font-size:14px;margin-bottom:1.5rem}
.hero-stats{display:flex;align-items:center;gap:0;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;overflow:hidden;display:inline-flex}
.hero-stat{padding:.75rem 1.5rem;text-align:center}
.hero-divider{width:1px;height:40px;background:rgba(255,255,255,0.12)}
.stat-num{display:block;font-size:1.5rem;font-weight:600;color:#e8b84b;line-height:1}
.stat-lbl{display:block;font-size:11px;color:rgba(255,255,255,0.5);margin-top:3px;letter-spacing:.5px}

/* Amenities bar */
.amenity-bar{background:#0f2240;border-top:1px solid rgba(255,255,255,0.08);padding:.75rem 1.5rem;overflow-x:auto}
.amenity-bar-inner{max-width:1080px;margin:0 auto;display:flex;gap:.5rem;flex-wrap:wrap}
.amenity-chip{display:flex;align-items:center;gap:5px;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:20px;padding:4px 10px;font-size:12px;color:rgba(255,255,255,0.7);white-space:nowrap;flex-shrink:0}
.amenity-icon{font-size:13px}

/* Body layout */
.book-body{max-width:1080px;margin:0 auto;padding:2rem 1.5rem 4rem;display:grid;grid-template-columns:1fr 320px;gap:2rem;align-items:start}
@media(max-width:820px){.book-body{grid-template-columns:1fr}.book-sidebar{order:-1}}

/* Timer banner */
.timer-banner{background:linear-gradient(90deg,#7c3a00,#b8882a);color:#fff;text-align:center;padding:.65rem 1.25rem;border-radius:6px;font-size:13px;margin-bottom:1rem}
.timer-banner strong{font-variant-numeric:tabular-nums;letter-spacing:.5px}

/* Steps */
.step-indicator{display:flex;align-items:center;margin-bottom:2rem}
.step-item{display:flex;align-items:center;gap:8px;flex:1}
.step-circle{width:28px;height:28px;border-radius:50%;border:2px solid #d0dae6;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;color:#8a9baa;flex-shrink:0;transition:all .2s}
.step-item.active .step-circle{border-color:#0f2240;background:#0f2240;color:#fff}
.step-item.done .step-circle{border-color:#1a6b3a;background:#1a6b3a;color:#fff}
.step-label{font-size:12px;color:#8a9baa;white-space:nowrap}
.step-item.active .step-label{color:#0f2240;font-weight:500}
.step-line{flex:1;height:1px;background:#d0dae6;margin:0 8px}
.step-panel{background:#fff;border:1px solid #d0dae6;border-radius:8px;padding:2rem}
.panel-title{margin-bottom:1.5rem}
.panel-title h2{font-family:'Playfair Display',Georgia,serif;font-size:1.4rem;font-weight:600;color:#0f2240;margin-bottom:.3rem}
.panel-title p{color:#5a6e82;font-size:14px}
.guests-row{display:flex;align-items:center;gap:1rem;margin:1.25rem 0;flex-wrap:wrap}
.guests-row label{font-size:14px;font-weight:500;color:#1c2b3a}
.guests-row select{border:1px solid #d0dae6;border-radius:5px;padding:.5rem .75rem;font-size:14px}
.occupancy-note{font-size:12px;color:#8a9baa}
.field-grid{display:grid;gap:1rem;margin-bottom:1rem}
.field-grid.two{grid-template-columns:1fr 1fr}
.field-grid.three{grid-template-columns:1fr 1fr 1fr}
@media(max-width:600px){.field-grid.two,.field-grid.three{grid-template-columns:1fr}}
.field-group{display:flex;flex-direction:column;gap:5px}
.field-group label{font-size:11px;font-weight:500;letter-spacing:.8px;text-transform:uppercase;color:#5a6e82}
.field-group input,.field-group select{border:1px solid #d0dae6;border-radius:5px;padding:.6rem .85rem;font-size:15px;color:#1c2b3a;outline:none;transition:border-color .15s;width:100%}
.field-group input:focus,.field-group select:focus{border-color:#0f2240;box-shadow:0 0 0 3px rgba(15,34,64,.08)}
.btn-row{display:flex;gap:1rem;margin-top:1.5rem;justify-content:flex-end;flex-wrap:wrap}
.primary-btn{background:#0f2240;color:#fff;border:none;border-radius:6px;padding:.75rem 1.75rem;font-size:15px;font-weight:500;cursor:pointer;transition:background .2s}
.primary-btn:hover:not(:disabled){background:#1a3560}
.primary-btn:disabled{background:#8a9baa;cursor:not-allowed}
.ghost-btn{background:transparent;color:#5a6e82;border:1px solid #d0dae6;border-radius:6px;padding:.75rem 1.25rem;font-size:14px;cursor:pointer}
.ghost-btn:hover{background:#f8f7f4}
.form-error{background:#fff0f0;border:1px solid #fcc;border-radius:5px;padding:.75rem 1rem;font-size:13.5px;color:#c0392b;margin-bottom:1rem}

/* Agreement */
.agreement-doc{border:1px solid #d0dae6;border-radius:6px;overflow:hidden;margin-bottom:1.5rem}
.doc-header{background:#0f2240;padding:1.5rem;text-align:center}
.doc-logo{color:#b8882a;font-size:11px;letter-spacing:3px;text-transform:uppercase;margin-bottom:.5rem}
.doc-title{color:#fff;font-family:'Playfair Display',Georgia,serif;font-size:1.4rem}
.doc-prop{color:rgba(255,255,255,0.5);font-size:12px;margin-top:.3rem}
.booking-recap{display:grid;grid-template-columns:repeat(3,1fr);border-bottom:1px solid #d0dae6}
@media(max-width:600px){.booking-recap{grid-template-columns:1fr 1fr}}
.recap-item{padding:.75rem 1rem;border-right:1px solid #d0dae6}
.recap-item:nth-child(3n){border-right:none}
.recap-item span{display:block;font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#8a9baa;margin-bottom:2px}
.recap-item strong{font-size:13px;color:#1c2b3a}
.legal-body{padding:1.25rem 1.5rem;max-height:400px;overflow-y:auto;border-bottom:1px solid #d0dae6}
.legal-clause{margin-bottom:1rem;padding-bottom:1rem;border-bottom:1px solid #f0f0f0}
.legal-clause:last-child{border-bottom:none}
.clause-title{font-size:11px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#0f2240;margin-bottom:.4rem}
.legal-clause p{font-size:13px;color:#5a6e82;line-height:1.65}
.initials-section{padding:1.25rem 1.5rem;border-bottom:1px solid #d0dae6;background:#f8f7f4}
.initials-label{font-size:11px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#5a6e82;margin-bottom:.85rem}
.initial-row{display:flex;align-items:center;gap:.75rem;margin-bottom:.6rem;font-size:13px;color:#1c2b3a}
.initial-input{width:56px;border:1px solid #d0dae6;border-radius:4px;padding:4px 8px;font-family:monospace;font-size:13px;text-align:center;text-transform:uppercase;flex-shrink:0}
.consent-section{padding:1.25rem 1.5rem;border-bottom:1px solid #d0dae6;display:flex;flex-direction:column;gap:.75rem}
.consent-item{display:flex;gap:10px;align-items:flex-start;font-size:13px;color:#1c2b3a;line-height:1.5;cursor:pointer}
.consent-item input[type=checkbox]{margin-top:2px;width:15px;height:15px;accent-color:#0f2240;flex-shrink:0}
.sig-section{padding:1.25rem 1.5rem}
.sig-label-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:.75rem}
.sig-label-row span{font-size:11px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#5a6e82}
.sig-mode-toggle{display:flex;border:1px solid #d0dae6;border-radius:5px;overflow:hidden}
.sig-mode-toggle button{font-size:12px;padding:4px 12px;border:none;background:#fff;cursor:pointer;color:#5a6e82}
.sig-mode-toggle button.active{background:#0f2240;color:#fff}
.canvas-wrap{position:relative;border:1px solid #d0dae6;border-radius:5px;overflow:hidden}
.sig-canvas{width:100%;height:150px;display:block;cursor:crosshair;touch-action:none}
.clear-sig{position:absolute;top:8px;right:8px;font-size:11px;border:1px solid #d0dae6;border-radius:4px;background:#fff;padding:2px 8px;cursor:pointer;color:#5a6e82}
.typed-sig{width:100%;border:none;border-bottom:1px solid #d0dae6;padding:1rem .5rem;font-family:'Playfair Display',Georgia,serif;font-size:1.6rem;color:#0f2240;background:transparent;outline:none}
.timestamp{margin-top:1rem;font-size:11px;color:#8a9baa;font-family:monospace}

/* Sidebar */
.book-sidebar{}
.sidebar-card{background:#fff;border:1px solid #d0dae6;border-radius:8px;overflow:hidden;position:sticky;top:1.5rem}
.prop-hero-img{background:linear-gradient(135deg,#0f2240 0%,#1a3560 100%);height:180px;text-align:center;display:flex;align-items:center;justify-content:center;flex-direction:column;position:relative;overflow:hidden}
.prop-hero-img-inner{font-size:3.5rem;margin-bottom:.5rem}
.prop-hero-img-label{font-size:11px;color:rgba(255,255,255,0.5);letter-spacing:1px}
.photo-count-badge{position:absolute;bottom:8px;right:8px;background:rgba(0,0,0,0.6);color:#fff;font-size:11px;padding:4px 10px;border-radius:20px;backdrop-filter:blur(4px)}
.lightbox-overlay{position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;justify-content:center}
.lightbox-close{position:absolute;top:1rem;right:1rem;background:rgba(255,255,255,0.12);border:none;color:#fff;font-size:1.4rem;width:40px;height:40px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:10}
.lightbox-close:hover{background:rgba(255,255,255,0.25)}
.lightbox-counter{position:absolute;top:1.1rem;left:50%;transform:translateX(-50%);color:rgba(255,255,255,0.5);font-size:13px}
.lightbox-img{max-height:70vh;max-width:90vw;object-fit:contain;border-radius:4px;flex-shrink:0}
.lightbox-nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(255,255,255,0.12);border:none;color:#fff;font-size:2.5rem;width:52px;height:52px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;line-height:1}
.lightbox-nav:hover{background:rgba(255,255,255,0.25)}
.lightbox-prev{left:1rem}.lightbox-next{right:1rem}
.lightbox-thumbs{position:absolute;bottom:1rem;left:50%;transform:translateX(-50%);display:flex;gap:6px;overflow-x:auto;max-width:90vw;padding:4px}
.lightbox-thumb{width:52px;height:40px;object-fit:cover;border-radius:3px;cursor:pointer;opacity:.5;border:2px solid transparent;flex-shrink:0;transition:opacity .15s,border-color .15s}
.lightbox-thumb.active,.lightbox-thumb:hover{opacity:1;border-color:#e8b84b}
.prop-name{font-family:'Playfair Display',Georgia,serif;font-size:1rem;font-weight:600;color:#0f2240;padding:1rem 1.25rem .25rem}
.prop-addr{font-size:12px;color:#5a6e82;padding:0 1.25rem}
.prop-meta{display:flex;gap:.75rem;font-size:12px;color:#1c2b3a;padding:.75rem 1.25rem;border-bottom:1px solid #d0dae6;flex-wrap:wrap}
.sidebar-divider{border:none;border-top:1px solid #d0dae6;margin:0}
.dates-recap{font-size:13px;color:#1c2b3a;padding:.85rem 1.25rem;display:flex;flex-direction:column;gap:5px}
.dates-recap span{color:#8a9baa;margin-right:6px}
.price-summary{font-size:13px;padding:.85rem 1.25rem}
.ps-row{display:flex;justify-content:space-between;margin-bottom:6px;color:#5a6e82}
.ps-row.total{color:#1c2b3a;font-size:14px;margin-top:6px}
.ps-row.deposit-row{color:#b8882a;margin-top:4px}
.ps-row.ps-surcharge{color:#c0392b;font-size:12px;font-style:italic}
.ps-row.ps-discount{color:#16a34a;font-size:13px;font-weight:600}
.ps-divider{border:none;border-top:1px solid #d0dae6;margin:8px 0}
.ps-note{font-size:11px;color:#8a9baa;margin-top:8px;line-height:1.5}
.trust-items{margin-top:1rem;display:flex;flex-direction:column;gap:6px}
.trust-item{font-size:12px;color:#5a6e82}
.trust-item a{color:#0f2240;text-decoration:none}
.trust-item a:hover{text-decoration:underline}

/* Payment */
.payment-amount-box{background:#f8f7f4;border:1px solid #d0dae6;border-radius:6px;padding:1.25rem 1.5rem;margin-bottom:1.5rem;text-align:center}
.pay-label{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#5a6e82;margin-bottom:.4rem}
.pay-amount{font-family:'Playfair Display',Georgia,serif;font-size:2.25rem;font-weight:600;color:#0f2240;line-height:1}
.pay-sub{font-size:12px;color:#8a9baa;margin-top:.4rem}
.pay-options{display:flex;flex-direction:column;gap:.75rem;margin-bottom:1.5rem}
.pay-option{border:2px solid #d0dae6;border-radius:8px;padding:1.1rem 1.25rem;cursor:pointer;transition:border-color .15s,background .15s}
.pay-option.selected{border-color:#0f2240;background:#f8f9fb}
.pay-option-top{display:flex;justify-content:space-between;align-items:center;gap:1rem}
.pay-option-title{font-size:15px;font-weight:600;color:#0f2240}
.pay-option-sub{font-size:12px;color:#8a9baa;margin-top:2px}
.pay-option-amount{font-family:'Playfair Display',Georgia,serif;font-size:1.5rem;font-weight:600;color:#0f2240;white-space:nowrap}
.pay-option-check{font-size:12px;color:#1a6b3a;font-weight:600;margin-top:.6rem}

/* Complete */
.complete-panel{text-align:center;padding:3rem 2rem}
.complete-icon{font-size:3.5rem;margin-bottom:1rem}
.complete-panel h2{font-family:'Playfair Display',Georgia,serif;font-size:1.75rem;color:#1a6b3a;margin-bottom:.5rem}
.complete-panel>p{color:#5a6e82;font-size:14px;max-width:520px;margin:0 auto 1.5rem}
.confirm-details{background:#f8f7f4;border:1px solid #d0dae6;border-radius:6px;padding:1rem 1.5rem;max-width:520px;margin:0 auto 1.25rem;text-align:left}
.cd-row{display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px;gap:1rem}
.cd-row span{color:#5a6e82;flex-shrink:0}
.cd-row strong{text-align:right}
.agreement-id{font-family:monospace;font-size:12px;color:#8a9baa;background:#f0f0f0;display:inline-block;padding:4px 12px;border-radius:4px;margin-bottom:1.25rem}
.next-steps{font-size:13px;color:#5a6e82;max-width:480px;margin:0 auto 1.5rem;line-height:1.6}
.next-steps a{color:#0f2240}

/* ── Mobile ── */
@media(max-width:640px){
  .prop-hero{padding:1.75rem 1rem 1.5rem}
  .prop-hero h1{font-size:1.55rem}
  .hero-stat{padding:.5rem .75rem}
  .stat-num{font-size:1.2rem}
  .book-body{padding:1rem .75rem 3rem;gap:1rem}
  .step-panel{padding:1.25rem 1rem}
  .panel-title h2{font-size:1.15rem}
  .step-label{display:none}
  .step-item{flex:none}
  .step-line{flex:1;margin:0 4px}
  .btn-row{flex-direction:column-reverse;gap:.75rem}
  .btn-row .primary-btn,.btn-row .ghost-btn{width:100%;min-height:52px;font-size:15px;text-align:center;justify-content:center}
  .primary-btn,.ghost-btn{min-height:52px}
  .field-group input,.field-group select{font-size:16px}
  .guests-row select{font-size:16px;min-height:48px;padding:.6rem .75rem}
  .guests-row{gap:.75rem}
  .legal-body{padding:.85rem 1rem;max-height:240px}
  .initials-section,.consent-section,.sig-section{padding:.85rem 1rem}
  .initial-input{min-height:48px;font-size:15px;width:60px}
  .consent-item input[type=checkbox]{width:20px;height:20px;flex-shrink:0}
  .consent-item{font-size:12.5px}
  .doc-header{padding:1.1rem}
  .doc-title{font-size:1.1rem}
  .booking-recap{grid-template-columns:1fr 1fr}
  .recap-item:nth-child(3n){border-right:1px solid #d0dae6}
  .recap-item:nth-child(even){border-right:none}
  .complete-panel{padding:2rem 1rem}
  .confirm-details{padding:.85rem 1rem}
  .sidebar-card{position:static}
  .sig-canvas{height:130px}
}
`}</style>
    </div>
  )
}
